import React, { useState, useEffect } from 'react';
import { ApolloClient } from 'apollo-client';
import { ApolloProvider } from '@apollo/react-hooks';
import { ApolloLink, split } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
  defaultDataIdFromObject,
  NormalizedCacheObject,
} from 'apollo-cache-inmemory';
import introspectionQueryResultData from './utilities/fragmentTypes.json';
import { CachePersistor } from 'apollo-cache-persist';
import { PersistentStorage, PersistedData } from 'apollo-cache-persist/types';
import { RestLink } from 'apollo-link-rest';
import { errorLink, authLink, trackLink, monitorLink, retryLink, contextLink } from 'services';
import { restApiUrl, graphqlApiUrl, appVersion, inImpersonateMode } from 'helpers/constants';
import { BootScreen } from 'screens/BootScreen';
import { initChromeTools } from 'services/chrome/init';
import { AsyncStorage } from 'services/asyncStorage';

// Subscriptions
import { WebSocketLink } from 'apollo-link-ws';
import { getMainDefinition } from 'apollo-utilities';
import { subscriptionClient } from 'services/apollo/subscriptions';
import { useFeatureFlagState } from 'context';

export const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

export function dataIdFromObject(object: any) {
  switch (object.__typename) {
    case 'File':
      return object.id;

    case 'Node':
      return object.id;

    case 'Folder':
      return object.id;

    case 'GroupMember':
      // For optimistically adding other people into groups
      if (!object.id) {
        return 'OtherPeople:' + object.email;
      } else {
        return object.id;
      }

    case 'Collaborator':
      // Using email as the id for collaborators
      if (!object.id) {
        return 'Collaborator:' + object.email;
      } else {
        return object.id;
      }

    case 'ShareTargetPerson':
      return 'ShareTargetPerson:' + object.id + '-' + object.nodeId;

    case 'ShareTargetGroup':
      return 'ShareTargetGroup:' + object.id + '-' + object.nodeId;

    case 'ShareTargetInfo':
      return 'ShareTargetInfo:' + object.nodeId;

    case 'GroupShareTargets':
      return 'GroupShareTargets:' + object.nodeId;

    case 'PeopleShareTargets':
      if (object.targets[0]?.isOrgMember) {
        return 'PeopleShareTargets-org:' + object.nodeId;
      } else {
        return 'PeopleShareTargets-otherPeople:' + object.nodeId;
      }

    default:
      return defaultDataIdFromObject(object);
  }
}

export const apolloCache = new InMemoryCache({
  fragmentMatcher,
  dataIdFromObject,
});

export const storage = inImpersonateMode
  ? (window.sessionStorage as PersistentStorage<PersistedData<NormalizedCacheObject>>)
  : (window.localStorage as PersistentStorage<PersistedData<NormalizedCacheObject>>);

export const apolloPersistor = new CachePersistor({
  cache: apolloCache,
  storage,
});

export const getApolloClient = async (forcePurge: boolean = false) => {
  const httpLink = new HttpLink({ uri: graphqlApiUrl });
  const restLink = new RestLink({ uri: restApiUrl });
  const wsLink = new WebSocketLink(subscriptionClient);

  const networkLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      const isSubscription =
        definition.kind === 'OperationDefinition' && definition.operation === 'subscription';

      return isSubscription ? true : false;
    },
    wsLink,
    httpLink
  );

  const link = ApolloLink.from([
    contextLink,
    trackLink,
    monitorLink,
    retryLink,
    errorLink,
    authLink,
    restLink,
    networkLink,
  ]);

  const cacheVersion = await AsyncStorage.get('apollo-cache-version');

  if (appVersion !== cacheVersion) {
    await apolloPersistor.purge();
    await AsyncStorage.set('apollo-cache-version', appVersion);
  } else {
    await apolloPersistor.restore();
  }

  return new ApolloClient<NormalizedCacheObject>({
    link,
    cache: apolloCache,
    resolvers: {
      ShareTargetInfo: {
        nodeId: (dataFromServer, _args, { context }) => {
          return dataFromServer.nodeId;
        },
      },
      GroupShareTargets: {
        nodeId: (dataFromServer, _args, { context }) => {
          return dataFromServer.nodeId;
        },
      },
      PeopleShareTargets: {
        nodeId: (dataFromServer, _args, { context }) => {
          return dataFromServer.nodeId;
        },
      },
      ShareTargetPerson: {
        nodeId: (dataFromServer, _args, { context }) => {
          return dataFromServer.nodeId;
        },
      },
    },
  });
};

export const Apollo = (props: any) => {
  const [client, setClient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { groupsFeatureFlag } = useFeatureFlagState();

  useEffect(() => {
    // resumes if previously paused (eg during ff change)
    apolloPersistor.resume();

    getApolloClient().then((client) => {
      setClient(client);
      setLoading(false);
    });
  }, [groupsFeatureFlag]);

  if (loading) {
    return <BootScreen />;
  }

  initChromeTools();

  return <ApolloProvider client={client}>{props.children}</ApolloProvider>;
};
